import {
  OrderWebService,
  RestfulOrderWebService,
  OrdersWithPagination,
} from 'ws/OrderWebService';

import { Order, State } from 'core/order/Order';
import { Pageable } from 'ws/Pageable';
import { AWSS3WebService, RestfulAWSS3WebService } from 'ws/AWSS3WebService';
import { Actor } from 'core/auth/Account';
import { Permission } from 'core/auth/Permission';
import { useOrderStore } from 'store/orderStore';

export interface OrderManager {
  getOrder(orderNumber: string): Promise<Order>;
  getOrderById(orderId: string | number): Promise<Order>;
  getOrders(
    advertiserId: number | null | undefined,
    pageable: Pageable,
    search?: string,
    deliveries?: string[],
  ): Promise<OrdersWithPagination>;
  activateOrders(orderId: number[]): Promise<void>;
  deactivateOrders(orderId: number[]): Promise<void>;
  approveOrders(orderId: number[]): Promise<void>;
  rejectOrders(orderId: number[]): Promise<void>;
  createOrder(order: Order): Promise<Order>;
  updateOrder(order: any): Promise<Order>;
  settleOrder(orderId: number): Promise<void>;
  getOrderName(orderId: number): Promise<string | null>;
  canOrderBeSettled(orderState: State): boolean;
  canOrderBeApproved(orderState: State): boolean;
  canOrderBeRejected(orderState: State): boolean;
  canOrderBeEdit(actor: Actor | null, order: Order): boolean;
}

export class DefaultOrderManager implements OrderManager {
  webService: OrderWebService;
  cloudStorageWebService: AWSS3WebService;

  constructor(
    webService: OrderWebService = new RestfulOrderWebService(),
    cloudStorageWebService: AWSS3WebService = new RestfulAWSS3WebService(),
  ) {
    this.webService = webService;
    this.cloudStorageWebService = cloudStorageWebService;
  }

  async getOrder(orderNumber: string): Promise<Order> {
    const order = await this.webService.getOrder(orderNumber);
    // zustand寫入Order
    useOrderStore.setState({ order: order });
    return order;
  }

  async getOrderById(orderId: string | number): Promise<Order> {
    return this.webService.getOrderById(orderId);
  }

  async getOrders(
    advertiserId: number | null | undefined,
    pageable: Pageable,
    search?: string,
    deliveries?: string[],
  ): Promise<OrdersWithPagination> {
    const ordersWithPagination = await this.webService.getOrders(
      advertiserId,
      pageable,
      search,
      deliveries,
    );
    return ordersWithPagination;
  }

  async activateOrders(orderIds: number[]) {
    await this.webService.activateOrders(orderIds);
  }

  async deactivateOrders(orderIds: number[]) {
    await this.webService.deactivateOrders(orderIds);
  }

  async approveOrders(orderIds: number[]) {
    await this.webService.approveOrders(orderIds);
  }

  async rejectOrders(orderIds: number[]) {
    await this.webService.rejectOrders(orderIds);
  }

  async createOrder(order: Order) {
    const newOrder = await this.webService.createOrder(order);
    return newOrder;
  }

  async updateOrder(order: any) {
    const updatedOrder = await this.webService.updateOrder(order);
    return updatedOrder;
  }

  async settleOrder(orderId: number) {
    await this.webService.settleOrder(orderId);
  }

  async getOrderName(orderId: number): Promise<string | null> {
    return this.webService.getOrderName(orderId);
  }

  canOrderBeSettled(orderState: State): boolean {
    return ![State.SETTLED, State.SETTLE].includes(orderState);
  }

  canOrderBeApproved(orderState: State): boolean {
    return [State.NOT_APPROVE, State.CHANGE_PENDING].includes(orderState);
  }

  canOrderBeRejected(orderState: State): boolean {
    return [State.NOT_APPROVE, State.CHANGE_PENDING].includes(orderState);
  }

  canOrderBeEdit(actor: Actor | null, order: Order): boolean {
    if (!actor) {
      return false;
    }
    return (
      ![State.SETTLE, State.SETTLED].includes(order.state) &&
      actor.permissions.indexOf(Permission.ORDER_WRITE) > -1
    );
  }
}
